import React, { FC } from 'react';

import { Box } from '@mui/material';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

type MarkdownVariablesType = Record<string, string>;

type Props = {
  markdown: string;
  variables?: MarkdownVariablesType;
};

const renderMarkdownWithVariables = (
  markdown: string,
  variables: MarkdownVariablesType = {},
): string =>
  markdown.replace(/\{\{([A-Z0-9_]+)\}\}/g, (placeholder, variableName) => {
    return variables[variableName] ?? placeholder;
  });

const LegalPage: FC<Props> = ({ markdown, variables }) => {
  const renderedMarkdown = renderMarkdownWithVariables(markdown, variables);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        color: 'text.primary',
        py: { xs: 4, md: 6 },
        px: { xs: 2, md: 3 },
      }}
    >
      <Box
        sx={{
          maxWidth: 920,
          mx: 'auto',
          '& h1': {
            color: 'text.primary',
            fontSize: { xs: '1.75rem', md: '2rem' },
            fontWeight: 700,
            lineHeight: 1.25,
            mt: 0,
            mb: 2,
          },
          '& h2': {
            color: 'text.primary',
            fontSize: { xs: '1.5rem', md: '1.625rem' },
            fontWeight: 700,
            lineHeight: 1.3,
            mt: 2.5,
            mb: 2.5,
          },
          '& h3': {
            color: 'text.primary',
            fontSize: { xs: '1.0625rem', md: '1.125rem' },
            fontWeight: 600,
            lineHeight: 1.4,
            mt: 4,
            mb: 1.5,
          },
          '& p': {
            color: 'text.secondary',
            lineHeight: 1.7,
            my: 2,
          },
          '& li': {
            color: 'text.secondary',
            lineHeight: 1.7,
            display: 'list-item',
          },
          '& ul, & ol': {
            pl: 5,
            my: 1.5,
          },
          '& ul': {
            listStyleType: 'disc',
          },
          '& ol': {
            listStyleType: 'decimal',
          },
          '& ul li': {
            listStyleType: 'disc',
          },
          '& ol li': {
            listStyleType: 'decimal',
          },
          '& a': { color: 'primary.main' },
          '& hr': {
            border: 'none',
            borderTop: '1px solid',
            borderColor: 'divider',
            mt: 5,
            mb: 4,
          },
        }}
      >
        <Markdown remarkPlugins={[remarkGfm]}>{renderedMarkdown}</Markdown>
      </Box>
    </Box>
  );
};

export default LegalPage;
